
import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface PlaceBasicInfoProps {
  name: string;
  setName: (name: string) => void;
  placeType: string;
  setPlaceType: (type: string) => void;
  rating: string;
  setRating: (rating: string) => void;
  priceRange: string;
  setPriceRange: (range: string) => void;
  isExistingPlace?: boolean;
}

export function PlaceBasicInfo({
  name,
  setName,
  placeType,
  setPlaceType,
  rating,
  setRating,
  priceRange,
  setPriceRange,
  isExistingPlace = false
}: PlaceBasicInfoProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Basic Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input 
            id="name" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            placeholder="Place name" 
            className={isExistingPlace ? "bg-gray-50" : ""}
            readOnly={isExistingPlace}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="placeType">Type</Label>
          <Select 
            value={placeType} 
            onValueChange={setPlaceType}
            disabled={isExistingPlace}
          >
            <SelectTrigger id="placeType" className={isExistingPlace ? "bg-gray-50" : ""}>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="restaurant">Restaurant</SelectItem>
              <SelectItem value="hotel">Hotel</SelectItem>
              <SelectItem value="museum">Museum</SelectItem>
              <SelectItem value="beach">Beach</SelectItem>
              <SelectItem value="park">Park</SelectItem>
              <SelectItem value="attraction">Attraction</SelectItem>
              <SelectItem value="shop">Shop</SelectItem>
              <SelectItem value="activity">Activity</SelectItem>
              <SelectItem value="event">Event</SelectItem>
              <SelectItem value="festival">Festival</SelectItem>
              <SelectItem value="landmark">Landmark</SelectItem>
              <SelectItem value="nightlife">Nightlife</SelectItem>
              <SelectItem value="viewpoint">Viewpoint</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="rating">Rating (1-5)</Label>
          <Input 
            id="rating" 
            type="number" 
            min="1" 
            max="5" 
            step="0.1" 
            value={rating} 
            onChange={(e) => setRating(e.target.value)}
            placeholder="4.5" 
            className={isExistingPlace ? "bg-gray-50" : ""}
            readOnly={isExistingPlace}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="priceRange">Price Range</Label>
          <Select 
            value={priceRange}
            onValueChange={setPriceRange}
            disabled={isExistingPlace}
          >
            <SelectTrigger id="priceRange" className={isExistingPlace ? "bg-gray-50" : ""}>
              <SelectValue placeholder="Select price range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="budget">Budget ($)</SelectItem>
              <SelectItem value="moderate">Moderate ($$)</SelectItem>
              <SelectItem value="expensive">Expensive ($$$)</SelectItem>
              <SelectItem value="luxury">Luxury ($$$$)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {isExistingPlace && (
        <p className="text-xs text-muted-foreground italic">
          These basic details are shared with the original place and cannot be modified.
        </p>
      )}
    </div>
  );
}
